import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type { HonorariosBillDetail, HonorariosBillListItem, HonorariosStatus } from '../types'


interface ListParams {
  status?: HonorariosStatus | string
  date_from?: string
  date_to?: string
  search?: string
  page?: number
  /** Campo de DRF OrderingFilter: 'issue_date' o '-issue_date'. Solo los
   *  declarados en `ordering_fields` de la vista; el resto los ignora en
   *  silencio, o sea la flecha se movería sin que la tabla cambie. */
  ordering?: string
}

export function useHonorariosList(params: ListParams = {}) {
  return useQuery<{ results: HonorariosBillListItem[]; count: number }>({
    queryKey: ['honorarios', 'list', params],
    queryFn: async () => {
      const r = await apiClient.get('/honorarios/', { params })
      return { results: r.data.results ?? r.data, count: r.data.count ?? (r.data.results?.length ?? 0) }
    },
    staleTime: 30_000,
  })
}

export function useHonorariosDetail(id: string | null) {
  return useQuery<HonorariosBillDetail>({
    queryKey: ['honorarios', 'detail', id],
    queryFn: async () => (await apiClient.get(`/honorarios/${id}/`)).data,
    enabled: !!id,
  })
}

export function useUploadHonorarios() {
  const qc = useQueryClient()
  return useMutation({
    // Los dos archivos son opcionales por separado: basta con uno. En la
    // práctica suele llegar solo el PDF — el SII manda los dos a la casilla del
    // receptor, pero el contratista que reenvía la boleta adjunta el impreso.
    mutationFn: async ({ xml, pdf }: { xml?: File | null; pdf?: File | null }) => {
      const fd = new FormData()
      if (xml) fd.append('xml', xml)
      if (pdf) fd.append('pdf', pdf)
      const r = await apiClient.post('/honorarios/upload/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data as HonorariosBillDetail
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

export function useUpdateHonorarios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<HonorariosBillDetail> }) => {
      const r = await apiClient.patch(`/honorarios/${id}/`, data)
      return r.data as HonorariosBillDetail
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

export function useDiscardHonorarios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post(`/honorarios/${id}/discard/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

export function usePostHonorarios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post(`/honorarios/${id}/post/`)
      return r.data as { voucher_id: string; total: string; retention: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

export interface PayHonorariosInput {
  payment_date: string
  /** Sin monto se paga el saldo completo. */
  amount?: string
  bank_account_id: string
  payment_method?: string
  reference?: string
}

export function usePayHonorarios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PayHonorariosInput }) => {
      const r = await apiClient.post(`/honorarios/${id}/pay/`, data)
      return r.data as {
        payment_id: string; payment_voucher_id: string; amount: string
        pending_amount: string; bill: HonorariosBillDetail
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

export function useCancelHonorariosPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, paymentId }: { id: string; paymentId: string }) =>
      (await apiClient.post(`/honorarios/${id}/payments/${paymentId}/cancel/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['honorarios'] }),
  })
}

